import { defineStore } from 'pinia';
import api, { refreshCsrf } from '../api/index.js';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,
        initialized: false,
        twoFactorRequired: false,
    }),

    getters: {
        isAuthenticated: (state) => !!state.user,
        organization: (state) => state.user?.organization ?? null,
        organizationRole: (state) => state.user?.organization?.role ?? null,
        isOwner: (state) => state.user?.organization?.role === 'owner',
        isAdmin: (state) => {
            const role = state.user?.organization?.role;
            return role === 'owner' || role === 'admin';
        },
        isViewer: (state) => state.user?.organization?.role === 'viewer',
        canManageSurveys: (state) => {
            const role = state.user?.organization?.role;
            return role === 'owner' || role === 'admin';
        },
    },

    actions: {
        async fetchUser() {
            try {
                const { data } = await api.get('/api/user');
                this.user = data;
            } catch {
                this.user = null;
            } finally {
                this.initialized = true;
            }
        },

        async login(credentials) {
            await refreshCsrf();
            try {
                const { data } = await api.post('/login', credentials);
                if (data?.two_factor) {
                    this.twoFactorRequired = true;
                    return { twoFactor: true };
                }
                await refreshCsrf();
                await this.fetchUser();
                return { success: true };
            } catch (error) {
                const data = error.response?.data;
                return { success: false, errors: data?.errors, message: data?.message };
            }
        },

        async register(data) {
            await refreshCsrf();
            await api.post('/register', data);
            await refreshCsrf();
            await this.fetchUser();
        },

        async logout() {
            await api.post('/logout');
            this.user = null;
            this.initialized = false;
        },

        async forgotPassword(email) {
            await refreshCsrf();
            return api.post('/forgot-password', { email });
        },

        async resetPassword(data) {
            await refreshCsrf();
            return api.post('/reset-password', data);
        },

        async twoFactorChallenge(code, recoveryCode = null) {
            await refreshCsrf();
            const payload = recoveryCode
                ? { recovery_code: recoveryCode }
                : { code };
            await api.post('/two-factor-challenge', payload);
            this.twoFactorRequired = false;
            await this.fetchUser();
        },
    },
});
